import React from 'react';
import { PageHeading, Heading, Text } from '../components/ui';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-neutral-50 px-0">
            <div className="max-w-4xl mx-auto py-8 px-4">
                <PageHeading>Privacy Policy</PageHeading>
                
                <div className="bg-neutral-0 hairline minimal-padding mt-8 space-y-6">
                    <Text size="sm" className="text-neutral-900 opacity-60">
                        Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </Text>

                    <div>
                        <Text size="sm" className="text-neutral-900">
                            This Privacy Policy explains how 4RKED ("we," "us," or "our") collects, uses, and protects your information when you use our website, mobile apps, and related services (the "Service").
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            By using the Service, you agree to the collection and use of information in accordance with this Policy.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>1. Information We Collect</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            We collect the following types of information:
                        </Text>

                        <div className="mt-4 space-y-4">
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">1.1 Account Information</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Name or display name</li>
                                    <li>Email address</li>
                                    <li>Password or authentication information (handled via our authentication provider; we never store your password in plain text)</li>
                                    <li>Profile settings (e.g., language, time zone)</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">1.2 Recipe & Content Information</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Recipes you create manually</li>
                                    <li>Recipes you import using Smart Transfer (including title, ingredients, instructions, notes, photos, and source URL/author when available)</li>
                                    <li>Tags, categories, and other metadata you assign</li>
                                    <li>Notes and comments you add to your recipes</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">1.3 Preferences & Dietary Information</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Dietary preferences (e.g., vegetarian, vegan, etc.)</li>
                                    <li>Allergy information (e.g., nuts, gluten, dairy)</li>
                                    <li>Other cooking preferences you choose to save</li>
                                </ul>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    This information may be considered sensitive. We collect it only when you choose to provide it, and we use it to personalize your experience (for example, to help filter or label recipes).
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">1.4 Usage Data</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Log data such as IP address, browser type, device type, pages viewed, and timestamps</li>
                                    <li>Interactions with the app (e.g., recipes viewed, saved, or shared)</li>
                                    <li>Crash reports and performance data</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">1.5 Cookies and Similar Technologies</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    We use cookies and similar technologies to:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Keep you logged in</li>
                                    <li>Remember your preferences</li>
                                    <li>Understand how the Service is used</li>
                                </ul>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    You can control cookies through your browser settings, but disabling them may affect your ability to use parts of the Service.
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>2. How We Use Your Information</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            We use the information we collect to:
                        </Text>

                        <div className="mt-4 space-y-3">
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">2.1 Provide and Operate the Service</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Create and manage your account</li>
                                    <li>Store and display your recipes, preferences, and saved content</li>
                                    <li>Sync your data across devices</li>
                                    <li>Facilitate recipe sharing according to your settings</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">2.2 Personalize Your Experience</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Recommend recipes and features based on your preferences, history, and settings</li>
                                    <li>Filter or label recipes according to your dietary preferences and allergies</li>
                                    <li>Customize the interface and content you see</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">2.3 Communicate with You</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Send account-related messages (e.g., verification emails, password reset, security alerts)</li>
                                    <li>Respond to your support requests and feedback</li>
                                    <li>Send optional updates or newsletters if you choose to receive them (you can opt out at any time)</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">2.4 Improve the Service</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Analyze usage patterns to understand how the Service is used</li>
                                    <li>Fix bugs, improve performance, and develop new features</li>
                                    <li>Conduct analytics and aggregate statistics (in a way that does not identify you personally)</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">2.5 Security and Legal Compliance</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Protect against, investigate, and deter fraudulent, unauthorized, or illegal activity</li>
                                    <li>Enforce our Terms of Service</li>
                                    <li>Comply with legal obligations and respond to lawful requests</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>3. How We Share Your Information</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4 font-medium">
                            We do not sell your personal information.
                        </Text>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            We may share your information in the following situations:
                        </Text>

                        <div className="mt-4 space-y-3">
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.1 With Service Providers</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    We may share information with trusted third-party providers who help us operate and improve the Service, such as:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Cloud hosting and database providers</li>
                                    <li>Email and notification providers</li>
                                    <li>Analytics and crash reporting services</li>
                                </ul>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    These providers are allowed to use your information only as necessary to provide services to us and are required to protect it.
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.2 When You Share Content</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    If you choose to:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Make a recipe public, or</li>
                                    <li>Share a recipe with friends or the community</li>
                                </ul>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    Then the content of that recipe (and your display name or profile information) may be visible to others according to your settings.
                                </Text>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    We do not share your allergy or dietary preference information with other users unless it is incorporated into content you choose to publish (for example, mentioning your allergies in a note or bio).
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.3 For Legal Reasons</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    We may disclose information if we believe it is reasonably necessary to:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Comply with applicable laws, regulations, legal processes, or government requests</li>
                                    <li>Protect the rights, property, or safety of 4RKED, our users, or the public</li>
                                    <li>Enforce our Terms of Service or address violations</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.4 Business Transfers</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    If we are involved in a merger, acquisition, sale of assets, or similar transaction, your information may be transferred as part of that transaction. We will make reasonable efforts to ensure the new entity honors this Privacy Policy.
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.5 With Your Consent</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    We may share your information for other purposes if you give us your explicit consent.
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>4. Data Security</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            We take reasonable measures to protect your information, including:
                        </Text>
                        <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-2">
                            <li>Using encryption in transit (HTTPS) for data exchanged between your device and our servers</li>
                            <li>Storing passwords using secure hashing algorithms, not in plain text</li>
                            <li>Restricting access to personal information to authorized personnel and service providers</li>
                            <li>Maintaining technical and organizational safeguards to protect against unauthorized access, loss, or misuse</li>
                        </ul>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            However, no method of transmission or storage is completely secure. While we strive to protect your information, we cannot guarantee absolute security.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>5. Data Retention</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            We retain your information for as long as your account is active or as needed to provide the Service, comply with our legal obligations, resolve disputes, and enforce our agreements.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            When you request deletion of your account, we will:
                        </Text>
                        <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                            <li>Delete or anonymize your personal information within a reasonable time, subject to any legal retention requirements</li>
                            <li>Remove or anonymize your recipes and associated personal data from active systems, while some information may remain in backup archives for a limited period</li>
                        </ul>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>6. Your Rights & Choices</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            Depending on your location, you may have certain rights regarding your personal information, such as:
                        </Text>
                        <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-2">
                            <li>Access: Request a copy of the personal information we hold about you</li>
                            <li>Correction: Request that we correct inaccurate or incomplete information</li>
                            <li>Deletion: Request that we delete your personal information</li>
                            <li>Restriction: Request that we limit how we process your information</li>
                            <li>Objection: Object to certain types of processing</li>
                            <li>Portability: Request a copy of your data in a structured, commonly used format</li>
                        </ul>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            You can often access and update much of your information directly in your account settings. For other requests, please contact us at the email listed below.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            If you are in the European Economic Area (EEA), United Kingdom, or a similar jurisdiction, you may also have the right to lodge a complaint with your local data protection authority.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA), such as the right to know and delete certain information. We do not sell your personal information.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>7. Children's Privacy</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            The Service is not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13. If we learn that we have collected such information, we will take steps to delete it.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>8. International Data Transfers</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            If you access the Service from outside the country where our servers are located, your information may be transferred to and processed in that country. By using the Service, you consent to such transfers, which may have different data protection laws than your jurisdiction.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>9. Changes to This Policy</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date above and may provide additional notice as required by law.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            Your continued use of the Service after any changes means you accept the updated Policy.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>10. Contact Us</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            If you have questions or requests regarding this Privacy Policy, please contact us at:
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-1">
                            Email: support@4rked.com
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;

